import React, { useEffect, useState } from "react"
import * as styles from "./Header.module.scss"
import { useIntl } from "gatsby-plugin-intl"
import { Link } from "../../Link"
import classNames from "classnames"
import Logo from "../../../assets/images/logo.svg"
import LogoWhite from "../../../assets/images/logo-white.svg"
import WhatsApp from "../../../assets/images/icons/whatsapp-icon.svg"
import BurgerMenu from "../../../assets/images/burger.svg"
import BurgerMenuClosed from "../../../assets/images/burger-closed.svg"
import LanguageSwitcher from "../../System/LanguageSwitcher/LanguageSwitcher"
import { useMixpanel } from "gatsby-plugin-mixpanel"

interface HeaderProps {
	isHome: boolean
	location: any
}

const menuItems = [
	{ name: "projects", path: "/projects/", translatePath: "header.navigation.projects" },
	{ name: "full-cycle-development", path: "/full-cycle-development/", translatePath: "header.navigation.full-cycle-development" },
	{ name: "about", path: "/about/", translatePath: "header.navigation.about" },
	{ name: "blog", path: "/blog/", translatePath: "header.navigation.blog" },
	{ name: "contact", path: "/contacts/", translatePath: "header.contact" },
]
const menuSecondItems = [
	{ name: "career", path: "/career/", translatePath: "header.navigation.career" },
	{ name: "press", path: "/press/", translatePath: "header.navigation.press" },
	{ name: "sitemap", path: "/sitemap/", translatePath: "header.navigation.sitemap" },
	{ name: "privacy", path: "/privacy/", translatePath: "header.navigation.privacy" },
]

const Header: React.FC<HeaderProps> = ({ isHome, location }) => {
	const intl = useIntl()
	const mixpanel = useMixpanel()
	const [currentPath, setCurrentPath] = useState<string>("")
	const pathname = location?.pathname ?? ""

	const [burgerActive, setBurgerActive] = useState(false)

	// sticky | hidden
	const [headerState, setHeaderState] = useState<"sticky" | "hidden">("sticky")
	const [isHeroVisible, setIsHeroVisible] = useState(false)

	let scrollPrev = 0
	let scrolled = 0
	let animationTick = false

	const burgerButtonClasses = classNames(styles.burgerButton, {
		[styles.burgerButtonActive]: burgerActive,
		[styles.burgerButtonWhite]: isHeroVisible && isHome
	})

	const headerContactsButton = classNames(
		styles.contactButton,
		{
			[styles.contactButtonWhite]: isHeroVisible && isHome,
			// [styles.headerHome]: !isHome,
		}
	)

	const mobileLogo = classNames(
		[styles.logo],
		[styles.logoMobile]
	)

	const headerClasses = classNames(
		styles.header,
		{
			[styles.headerSticky]: headerState === "sticky",
			[styles.headerHidden]: headerState === "hidden",
			[styles.headerWhite]: isHeroVisible && isHome,
			// [styles.headerHome]: !isHome,
		}
	)

	// Burger Menu Logic
	useEffect(() => {
		if (document?.body?.classList.contains("with-burger-active")) {
			document.body.classList.remove("with-burger-active")
		}
	}, [])

	useEffect(() => {
		const path = pathname.split("/")[2]
		if (path) setCurrentPath(`/${path}/`)
	}, [pathname])

	const onBurgerToggle = () => {
		if (!burgerActive) {
			document.body.classList.add("with-burger-active")
		} else {
			document.body.classList.remove("with-burger-active")
		}
		setBurgerActive(!burgerActive)
	}

	const burgerSrc = burgerActive ? BurgerMenuClosed : BurgerMenu

	// Sticky Header Logic
	const update = () => {
		animationTick = false
		scrolled = window.scrollY

		if (scrolled < scrollPrev) {
			// скроллим вверх → показать
			setHeaderState("sticky")
		} else if (scrolled > scrollPrev && scrolled > 60) {
			// скроллим вниз (и не в самом верху) → спрятать
			setHeaderState("hidden")
		}
	}

	const onHeaderScroll = () => {
		scrollPrev = scrolled
		requestTick()
	}

	const requestTick = () => {
		if (!animationTick) {
			requestAnimationFrame(update)
		}
		animationTick = true
	}

	useEffect(() => {
		window.addEventListener("scroll", onHeaderScroll)
		return () => window.removeEventListener("scroll", onHeaderScroll)
	}, [pathname])

	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.scrollY
			setIsHeroVisible(scrollTop < 80)
		}

		handleScroll()
		window.addEventListener("scroll", handleScroll)
		return () => window.removeEventListener("scroll", handleScroll)
	}, [])

	const onContactUs = () => {
		if (burgerActive) onBurgerToggle()
		mixpanel.track('Click on "Contact us" button')
	}

	return (
		<header className={headerClasses}>
			<div className={styles.container}>

				<Link to="/" className={styles.logo}>
					<img
						className={styles["logo-image"]}
						src={isHeroVisible && isHome ? LogoWhite : Logo}
						alt="Inventale logo"
						width="110"
						height="26"
					/>
				</Link>

				<button
					type="button"
					className={burgerButtonClasses}
					onClick={onBurgerToggle}
				>
					{/* <img src={burgerSrc} alt="Menu icon" width="24" height="24" /> */}
					<div className={styles.burgerButtonIcon}>
						<span className={styles.burgerButtonLine}></span>
						<span className={styles.burgerButtonLine}></span>
					</div>
				</button>


				<nav className={styles.navigation}>
					<Link to="/" className={mobileLogo}>
						<img
							className={styles["logo-image"]}
							src={Logo}
							alt="Inventale logo"
							width="110"
							height="26"
						/>
					</Link>
					<a href="tel:+971585951846" className={styles.phone}>+971 58 595 1846
						<img className={styles.phoneIcon} src={WhatsApp} alt="whatsapp" width={16} height={16} />
					</a>
					<ul className={styles.navigationList}>
						{menuItems.map((item, index) => (
							<li className={styles.navigationListItem} key={index}>
								<Link
									to={
										item.name === "blog"
											? item.path.replace("{locale}", intl.locale)
											: item.path
									}
									className={classNames(styles.navigationLink, {
										[styles.navigationLinkActive]: item.path === currentPath,
									})}
								>
									{intl.formatMessage({ id: item.translatePath })}
								</Link>
							</li>
						))}
					</ul>
					<LanguageSwitcher location={location} />
					<Link to="/contacts"
						className={headerContactsButton}
						color={"white"}
					>
						{intl.formatMessage({ id: "header.contact" })}
					</Link>
					<a className={styles.address} href="https://maps.app.goo.gl/P3hw76Srm5Rx7UXRA" target="_blank">
						UAE, Fujairah - Creative <br />Tower P.O. Box 4422 Fujairah
					</a>
					<ul className={styles.privacy}>
						{
							menuSecondItems.map((item, index) => (
								<li key={index}>
									<Link
										to={item.path}
										className={classNames(styles.navigationLink, {
											[styles.navigationLinkActive]: item.path === currentPath,
										})}
									>
										{intl.formatMessage({ id: item.translatePath })}
									</Link>
								</li>
							))
						}
					</ul>
				</nav>

			</div>
		</header>
	)
}

export default Header
